import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  MenuItem,
  IconButton,
  Chip,
  useTheme,
  InputAdornment,
} from '@mui/material';
import {  Edit, Delete, Search } from '@mui/icons-material';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_PaginationState,
} from 'material-react-table';
import { useAppDispatch, useAppSelector } from '@/store';
import {
  fetchCompanies,
  createCompany,
  updateCompany,
  deleteCompany,
  clearCompanyError,
} from '@/store';
import { Company, CreateCompanyRequest, UpdateCompanyRequest } from '@/constants/types';
import { CompanyDialog } from './CompanyDialog';
import { ConfirmDialog } from '@/components/common';

export const CompanyManagement: React.FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const {
    companies,
    pagination,
    isLoading,
    isCreating,
    isUpdating,
    error,
  } = useAppSelector((state) => state.company);

  const [globalFilter, setGlobalFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [paginationState, setPaginationState] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState<Company | null>(null);

  useEffect(() => {
    loadCompanies();
  }, [globalFilter, statusFilter, paginationState]);

  const loadCompanies = () => {
    const params: any = {
      page: paginationState.pageIndex + 1,
      limit: paginationState.pageSize,
      sort: '-createdAt',
    };

    if (globalFilter) {
      params.search = globalFilter;
    }

    if (statusFilter !== 'all') {
      params.isActive = statusFilter === 'active';
    }

    dispatch(fetchCompanies(params));
  };

  const handleCreateClick = () => {
    setSelectedCompany(null);
    setDialogOpen(true);
  };

  const handleEditClick = (company: Company) => {
    setSelectedCompany(company);
    setDialogOpen(true);
  };

  const handleDeleteClick = (company: Company) => {
    setCompanyToDelete(company);
    setDeleteDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedCompany(null);
  };

  const handleDialogSubmit = async (data: CreateCompanyRequest | UpdateCompanyRequest) => {
    try {
      if (selectedCompany) {
        await dispatch(updateCompany({ 
          id: selectedCompany._id, 
          data: data as UpdateCompanyRequest 
        })).unwrap();
      } else {
        await dispatch(createCompany(data as CreateCompanyRequest)).unwrap();
      }
      handleDialogClose();
      loadCompanies();
    } catch (error) {
      console.error('Failed to save company:', error);
    }
  };

  const handleDeleteConfirm = async () => {
    if (companyToDelete) {
      try {
        await dispatch(deleteCompany(companyToDelete._id)).unwrap();
        setDeleteDialogOpen(false);
        setCompanyToDelete(null);
        loadCompanies();
      } catch (error) {
        console.error('Failed to delete company:', error);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setCompanyToDelete(null);
  };

  const handleErrorDismiss = () => {
    dispatch(clearCompanyError());
  };

  const columns = useMemo<MRT_ColumnDef<Company>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Company Name',
        size: 200,
        Cell: ({ cell }) => (
          <Typography variant="body2" fontWeight="medium">
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      // {
      //   accessorKey: 'description',
      //   header: 'Description',
      //   size: 250,
      //   Cell: ({ cell }) => (
      //     <Typography variant="body2" color="text.secondary">
      //       {cell.getValue<string>()}
      //     </Typography>
      //   ),
      // },
      // {
      //   accessorKey: 'city',
      //   header: 'City',
      //   size: 120,
      //   Cell: ({ cell }) => (
      //     <Typography variant="body2" color="text.secondary">
      //       {cell.getValue<string>()}
      //     </Typography>
      //   ),
      // },
      // {
      //   accessorKey: 'state',
      //   header: 'State',
      //   size: 120,
      //   Cell: ({ cell }) => (
      //     <Typography variant="body2" color="text.secondary">
      //       {cell.getValue<string>()}
      //     </Typography>
      //   ),
      // },
      {
        accessorKey: 'email',
        header: 'Email',
        size: 200,
        Cell: ({ cell }) => (
          <Typography variant="body2" color="text.secondary">
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: 'mobile',
        header: 'Mobile',
        size: 120,
        Cell: ({ cell }) => (
          <Typography variant="body2" color="text.secondary">
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: 'isActive',
        header: 'Status',
        size: 120,
        enableSorting: true,
        Cell: ({ cell }) => (
          <Chip
            label={cell.getValue() ? 'Active' : 'Inactive'}
            color={cell.getValue() ? 'success' : 'default'}
            size="small"
          />
        ),
      },
      // {
      //   accessorKey: 'createdAt',
      //   header: 'Created Date',
      //   size: 150,
      //   Cell: ({ cell }) => (
      //     <Typography variant="body2" color="text.secondary">
      //       {new Date(cell.getValue<string>()).toLocaleDateString()}
      //     </Typography>
      //   ),
      // },
      {
        id: 'actions',
        header: 'Actions',
        size: 100,
        enableSorting: false,
        Cell: ({ row }) => (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              size="small"
              onClick={() => handleEditClick(row.original)}
              sx={{ color: 'primary.main' }}
            >
              <Edit fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => handleDeleteClick(row.original)}
              sx={{ color: 'error.main' }}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Box>
        ),
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data: companies,
    enableRowSelection: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableGlobalFilter: false,
    enablePagination: true,
    enableSorting: true,
    enableBottomToolbar: true,
    enableTopToolbar: false,
    manualPagination: true,
    rowCount: pagination?.totalItems || 0,
    muiTableBodyRowProps: {
      sx: {
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
      },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        border: `1px solid ${theme.palette.divider}`,
      },
    },
    muiTableProps: {
      sx: {
        tableLayout: 'fixed',
      },
    },
    state: {
      isLoading,
      pagination: paginationState,
    },
    onPaginationChange: setPaginationState,
  });

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight="bold" sx={{ mb: 3 }}>
        Company Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={handleErrorDismiss}>
          {error}
        </Alert>
      )}

      {/* Controls above table */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Search companies..."
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ minWidth: 300 }}
            size="small"
          />
          
          <TextField
            select
            size="small"
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
        </Box>

        <Button
          variant="contained"
          // startIcon={<Add />}
          onClick={handleCreateClick}
          disabled={isLoading}
        >
          Add Company
        </Button>
      </Box>

      <MaterialReactTable table={table} />

      <CompanyDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSubmit={handleDialogSubmit}
        company={selectedCompany}
        isLoading={isCreating || isUpdating}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Company"
        message={`Are you sure you want to delete "${companyToDelete?.name}"? This action cannot be undone.`}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        severity="error"
      />
    </Box>
  );
};